import { api } from "./api";
import { clerkClient } from "@/App";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

export interface FlightRestrictionZone {
  frz_id: number;
  name: string;
  type: "Prohibited" | "Restricted" | "Danger";
  source: string;
  version: string;
  effective_from: string;
  effective_to: string | null;
  active: boolean;
  last_updated: string;
  h3_indexes: string[] | null;
  data: Record<string, unknown>;
  geometry: string;
}

export interface FlightRestrictionZonesResponse {
  limit: number;
  offset: number;
  total: number;
  zones: FlightRestrictionZone[];
  totals: {
    total_zones: number;
    active_zones: number;
    total_per_type: {
      Danger: number;
      Prohibited: number;
      Restricted: number;
    };
  };
}

export interface FlightRestrictionZonesParams {
  type?: "Prohibited" | "Restricted" | "Danger";
  status?: "active" | "inactive";
  bounds?: string; // Format: "minLon,minLat,maxLon,maxLat"
  token?: string;
  limit?: number;
  offset?: number;
}

export interface FlightRestrictionZoneContainingPointParams {
  lat: number;
  lon: number;
}

// API functions
export const fetchFlightRestrictionZones = async (
  params?: FlightRestrictionZonesParams
): Promise<FlightRestrictionZonesResponse> => {
  const authToken = await clerkClient.session?.getToken();
  
  const queryParams = new URLSearchParams();
  if (params?.type) queryParams.append("type", params.type);
  if (params?.bounds) queryParams.append("bounds", params.bounds);
  if (params?.token) queryParams.append("token", params.token);
  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.offset) queryParams.append("offset", params.offset.toString());
  
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  
  const response = await api.get(`/v0/flight-restriction-zones${queryString}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return response.data;
};

export const fetchFlightRestrictionZoneById = async (
  id: number
): Promise<GeoJSON.Feature> => {
  const authToken = await clerkClient.session?.getToken();
  
  const response = await api.get(`/v0/flight-restriction-zones/${id}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return response.data;
};

export const fetchFlightRestrictionZonesContainingPoint = async (
  params: FlightRestrictionZoneContainingPointParams
): Promise<FlightRestrictionZonesResponse> => {
  const authToken = await clerkClient.session?.getToken();
  
  const queryParams = new URLSearchParams();
  queryParams.append("lat", params.lat.toString());
  queryParams.append("lon", params.lon.toString());
  
  const response = await api.get(`/v0/flight-restriction-zones/containing-point?${queryParams.toString()}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return response.data;
};

// Main query for flight restriction zones with proper caching
export const useFlightRestrictionZonesQuery = (params?: FlightRestrictionZonesParams) => {
  return useQuery({
    queryKey: ["flight-restriction-zones", params],
    queryFn: () => fetchFlightRestrictionZones(params),
    staleTime: 30 * 60 * 1000, // 30 minutes - flight restriction zones don't change often
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus for static data
  });
};

// Hook to get cached zones data without refetching
export const useFlightRestrictionZonesData = () => {
  const queryClient = useQueryClient();
  
  return useMemo(() => {
    // Try to get data from cache first
    const cachedData = queryClient.getQueryData<FlightRestrictionZonesResponse>(["flight-restriction-zones"]);
    if (cachedData) {
      return { data: cachedData, isLoading: false, error: null };
    }
    
    // If not in cache, return loading state
    return { data: null, isLoading: true, error: null };
  }, [queryClient]);
};

export const useFlightRestrictionZoneQuery = (id: number) => {
  return useQuery({
    queryKey: ["flight-restriction-zone", id],
    queryFn: () => fetchFlightRestrictionZoneById(id),
    enabled: !!id,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });
};

export const useFlightRestrictionZonesContainingPointQuery = (
  params: FlightRestrictionZoneContainingPointParams
) => {
  return useQuery({
    queryKey: ["flight-restriction-zones-containing-point", params],
    queryFn: () => fetchFlightRestrictionZonesContainingPoint(params),
    enabled: !!params.lat && !!params.lon,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });
};
